import {useEffect, useState} from 'react';

import {Favorite, InfoOutlined} from '@mui/icons-material';

import {SendIcon} from '../assets/icons';
import type {CompanyMatchResult} from '../types';

import {CultureTypeCard} from './CultureTypeCard';
import {MatchProgressBar} from './MatchProgressBar';

import './CompanyMatching.css';

const LOADING_MESSAGE_INTERVAL_MS = 2500;
const COMPLETING_MESSAGE_DURATION_MS = 500;

interface MatchState {
  companyName: string;
  match: CompanyMatchResult;
}

interface Props {
  heading: string;
  description: string;
  inputPlaceholder: string;
  matchResultHeading: (companyName: string) => string;
  youLabel: string;
  locale: 'de' | 'en';
  matchLimitReached?: boolean;
  /** Five messages to rotate through while the match is loading (localized by parent). */
  loadingMessages: [string, string, string, string, string];
  onGetMatch: (companyName: string) => Promise<{match: CompanyMatchResult}>;
}

const LikesIcon = () => <Favorite className='company-matching__likes-dislikes-icon' aria-hidden />;
const DislikesIcon = () => <InfoOutlined className='company-matching__likes-dislikes-icon' aria-hidden />;

export const CompanyMatching = ({
  heading,
  description,
  inputPlaceholder,
  matchResultHeading,
  youLabel,
  locale,
  matchLimitReached = false,
  loadingMessages,
  onGetMatch,
}: Props) => {
  const [input, setInput] = useState('');
  const [matchState, setMatchState] = useState<MatchState | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCompletingMessage, setShowCompletingMessage] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loading) return;
    const id = setInterval(() => {
      setLoadingMessageIndex((i) => (i + 1) % loadingMessages.length);
    }, LOADING_MESSAGE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [loading, loadingMessages]);

  useEffect(() => {
    if (loading) return;

    setShowCompletingMessage(true);
    const id = setTimeout(() => setShowCompletingMessage(false), COMPLETING_MESSAGE_DURATION_MS);

    return () => clearTimeout(id);
  }, [loading]);

  const handleGetMatch = async () => {
    const name = input.trim();
    if (!name || loading || matchLimitReached) return;

    setError(null);
    setLoadingMessageIndex(0);
    setLoading(true);
    try {
      const result = await onGetMatch(name);
      setMatchState({companyName: name, match: result.match});
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleGetMatch();
  };

  const match = matchState?.match;
  const cm = match?.culturalMapping;
  const displayCompanyName = match?.companyName || matchState?.companyName || '';

  return (
    <section className='company-matching'>
      <h2 className='company-matching__heading'>{heading}</h2>
      <p className='company-matching__description'>{description}</p>

      <div className='company-matching__input-row'>
        <input
          className='company-matching__input'
          type='text'
          value={input}
          placeholder={inputPlaceholder}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading || matchLimitReached}
        />
        <button
          className='company-matching__send-btn'
          onClick={handleGetMatch}
          disabled={!input.trim() || loading || matchLimitReached}
          aria-label='Get match'
        >
          <SendIcon />
        </button>
      </div>

      {error && <p className='company-matching__error'>{error}</p>}

      {(loading || showCompletingMessage) && (
        <div className='company-matching__loading' role='status' aria-live='polite'>
          <MatchProgressBar animating={loading} success={showCompletingMessage} />
          <p className='company-matching__loading-text'>
            {showCompletingMessage ? loadingMessages[4] : loadingMessages[loadingMessageIndex]}
          </p>
        </div>
      )}

      {matchState && !loading && !showCompletingMessage && match && cm && (
        <div className='company-matching__result'>
          <h3 className='company-matching__result-heading'>{matchResultHeading(displayCompanyName)}</h3>
          <div className='company-matching__visual'>
            <CultureTypeCard
              imageUrl={match.talent_culture_type_info?.image_url}
              name={match.talent_culture_type_info?.name[locale] ?? cm.talentCultureType.title ?? youLabel}
              label={youLabel}
            />
            <div className='company-matching__connector'>
              <div className='company-matching__line' />
              <div className='company-matching__circle'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  viewBox='0 0 132 132'
                  fill='none'
                  className='company-matching__circle-svg'
                >
                  <circle cx={66} cy={66} r={64} fill='#FFF5E8' stroke='#FFBF3E' strokeWidth={4} />
                </svg>
                <span className='company-matching__percentage'>{match.matchPercentage}%</span>
              </div>
              <div className='company-matching__line' />
            </div>
            <CultureTypeCard
              imageUrl={match.company_culture_type_info?.image_url}
              name={displayCompanyName}
              label={match.company_culture_type_info?.name[locale] ?? cm.companyCultureType.title ?? displayCompanyName}
            />
          </div>
          <div className='company-matching__match-text'>
            <section className='company-matching__match-text-section'>
              <h4 className='company-matching__match-text-heading'>{cm.introduction.title}</h4>
              <div className='company-matching__match-text-body'>
                <p>{cm.introduction.description}</p>
              </div>
            </section>
            <section className='company-matching__match-text-section'>
              <h4 className='company-matching__match-text-heading'>{cm.talentCultureType.title}</h4>
              <div className='company-matching__match-text-body'>
                <p>{cm.talentCultureType.description}</p>
              </div>
            </section>
            <section className='company-matching__match-text-section'>
              <h4 className='company-matching__match-text-heading'>{cm.companyCultureType.title}</h4>
              <div className='company-matching__match-text-body'>
                <p>{cm.companyCultureType.description}</p>
              </div>
            </section>
            <div className='company-matching__likes-dislikes-block'>
              <section
                className='company-matching__match-text-section
                company-matching__match-text-section--likes'
              >
                <div className='company-matching__likes-dislikes-header'>
                  <LikesIcon />
                  <h4 className='company-matching__match-text-heading'>{cm.likes.title}</h4>
                </div>
                <ul className='company-matching__likes-dislikes-list'>
                  {cm.likes.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </section>
              <section
                className='company-matching__match-text-section
                company-matching__match-text-section--dislikes'
              >
                <div className='company-matching__likes-dislikes-header'>
                  <DislikesIcon />
                  <h4 className='company-matching__match-text-heading'>{cm.dislikes.title}</h4>
                </div>
                <ul className='company-matching__likes-dislikes-list'>
                  {cm.dislikes.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </section>
            </div>
            <section className='company-matching__match-text-section'>
              <h4 className='company-matching__match-text-heading'>{cm.applicationRecommendation.title}</h4>
              <div className='company-matching__match-text-body'>
                <p>{cm.applicationRecommendation.description}</p>
              </div>
              <ul className='company-matching__likes-dislikes-list'>
                {cm.applicationRecommendation.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      )}
    </section>
  );
};
